import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LifecycleRuntimeProvider } from './contexts/LifecycleRuntimeContext';
import { ProtectedRoute } from './utils/ProtectedRoute';
import { AccountCapabilityProtectedRoute } from './utils/CapabilityProtectedRoute';
import { Toaster } from './components/ui/sonner';
import TawkToWidget from './components/TawkToWidget';
import './App.css';
import { exposeBuildMetadataOnWindow, logBuildMetadata } from './utils/buildMetadata';

exposeBuildMetadataOnWindow();

// ClearForm SPA is retained in-repo for internal/admin reuse only; public `/clearform/*` redirects (not in active use).

// Public Website Pages (NEW)
import {
  HomePage,
  CVPLandingPage,
  ServicesHubPage,
  ServiceDetailPage,
  ServicesCataloguePage,
  ServiceOrderPage,
  OrderSuccessPage,
  PricingPage,
  BookingPage,
  InsightsHubPage,
  AboutPage,
  ContactPage,
  CareersPage,
  PartnershipsPage,
  TalentPoolWizard,
  PartnershipEnquiryForm,
  PrivacyPage,
  TermsPage,
  CookiePolicyPage,
  ServicesHubPageCMS,
  CategoryPageCMS,
  ServicePageCMS,
} from './pages/public';

// New public pages
import AssureStackPage from './pages/public/AssureStackPage';
import AccessibilityPage from './pages/public/AccessibilityPage';
import FAQPage from './pages/public/FAQPage';
import NewsletterPage from './pages/public/NewsletterPage';
import ChecklistThankYouPage from './pages/public/ChecklistThankYouPage';
import UnsubscribePage from './pages/public/UnsubscribePage';
import RiskCheckPage from './pages/public/RiskCheckPage';
import LeadActivityTrackPage from './pages/public/LeadActivityTrackPage';
import ContractorRegisterPage from './pages/public/ContractorRegisterPage';

// Auth & Onboarding pages
import PortalSelectorPage from './pages/PortalSelectorPage';
import ClientLoginPage from './pages/ClientLoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import LoginPage from './pages/LoginPage'; // Legacy - keep for backwards compatibility
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ContractorLoginPage from './pages/contractor/ContractorLoginPage';
import ContractorSetPasswordPage from './pages/contractor/ContractorSetPasswordPage';
import ContractorDashboardPage from './pages/contractor/ContractorDashboardPage';
import JobPage from './pages/contractor/JobPage';
import IntakePage from './pages/IntakePage';
import OnboardingStatusPage from './pages/OnboardingStatusPage';
import OnboardingContinuePage from './pages/OnboardingContinuePage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import AdminBlogPage from './pages/AdminBlogPage';

// Client pages
import ClientDashboard from './pages/ClientDashboard';
import ClientCommandCenterPage from './pages/ClientCommandCenterPage';
import PropertiesPage from './pages/PropertiesPage';
import RequirementsPage from './pages/RequirementsPage';
import ComplianceScorePage from './pages/ComplianceScorePage';
import AssistantPage from './pages/AssistantPage';
import ProfilePage from './pages/ProfilePage';
import JurisdictionSettingsPage from './pages/JurisdictionSettingsPage';
import PropertyCreatePage from './pages/PropertyCreatePage';
import DocumentsPage from './pages/DocumentsPage';
import BulkUploadPage from './pages/BulkUploadPage';
import ReportsPage from './pages/ReportsPage';
import ReportsAuditPackPage from './pages/ReportsAuditPackPage';
import ReportsPropertyEvidencePage from './pages/ReportsPropertyEvidencePage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import ClientNotificationInboxPage from './pages/ClientNotificationInboxPage';
import CalendarPage from './pages/CalendarPage';
import TenantDashboard from './pages/TenantDashboard';
import TenantPropertiesPage from './pages/TenantPropertiesPage';
import TenantPropertyDetailPage from './pages/TenantPropertyDetailPage';
import TenantSettingsPage from './pages/TenantSettingsPage';
import TenantManagementPage from './pages/TenantManagementPage';
import ClientTenantComplianceDeliveryPage from './pages/ClientTenantComplianceDeliveryPage';
import BulkPropertyImportPage from './pages/BulkPropertyImportPage';
import IntegrationsPage from './pages/IntegrationsPage';
import BrandingSettingsPage from './pages/BrandingSettingsPage';
import BillingPage from './pages/BillingPage';
import ClientProvideInfoPage from './pages/ClientProvideInfoPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import ClientMaintenancePage from './pages/ClientMaintenancePage';
import ClientJobDetailPage from './pages/ClientJobDetailPage';
import ClientContractorsPage from './pages/ClientContractorsPage';
import ClientIssuesPage from './pages/ClientIssuesPage';
import ClientIssueDetailPage from './pages/ClientIssueDetailPage';
import ClientRiskSignalsPage from './pages/ClientRiskSignalsPage';
import ClientRentOperationsPage from './pages/ClientRentOperationsPage';
import ClientApprovalsPage from './pages/ClientApprovalsPage';
import ClientOrgComplianceReviewPage from './pages/ClientOrgComplianceReviewPage';
import ViewOrderPage from './pages/ViewOrderPage';
import UnifiedIntakeWizard from './pages/UnifiedIntakeWizard';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrderProvideInfoPublicPage from './pages/OrderProvideInfoPublicPage';
import OrderCheckoutPage from './pages/OrderCheckoutPage';
import ClientAuditLogPage from './pages/ClientAuditLogPage';
import ClientTasksPage from './pages/ClientTasksPage';
import ClientWorkQueuePage from './pages/ClientWorkQueuePage';

// Admin pages
import AdminDashboard from './pages/AdminDashboard';
import AdminAssistantPage from './pages/AdminAssistantPage';
import AdminBillingPage from './pages/AdminBillingPage';
import AdminPilotInvitesPage from './pages/admin/AdminPilotInvitesPage';
import AdminDiscoveryReviewPage from './pages/admin/discovery/AdminDiscoveryReviewPage';
import AdminPilotInviteDetailPage from './pages/admin/AdminPilotInviteDetailPage';
import AdminPilotOperationsPage from './pages/admin/AdminPilotOperationsPage';
import AdminPilotAccountDetailPage from './pages/admin/AdminPilotAccountDetailPage';
import AdminPilotAnomaliesPage from './pages/admin/AdminPilotAnomaliesPage';
import AdminPendingPaymentsPage from './pages/AdminPendingPaymentsPage';
import AdminOrdersPage from './pages/AdminOrdersPage';
import AdminServiceCataloguePage from './pages/AdminServiceCataloguePage';
import AdminNotificationPreferencesPage from './pages/AdminNotificationPreferencesPage';
import AdminNotificationInboxPage from './pages/AdminNotificationInboxPage';
import AdminIntakeSchemaPage from './pages/AdminIntakeSchemaPage';
import AdminAnalyticsDashboard from './pages/AdminAnalyticsDashboard';
import AdminExecutiveOverviewPage from './pages/AdminExecutiveOverviewPage';
import AdminGenerationHealthPage from './pages/AdminGenerationHealthPage';
import AdminSupportPage from './pages/AdminSupportPage';
import AdminKnowledgeBasePage from './pages/AdminKnowledgeBasePage';
import AdminLegalContentPage from './pages/AdminLegalContentPage';
import AdminCannedResponsesPage from './pages/AdminCannedResponsesPage';
import AdminLeadsPage from './pages/AdminLeadsPage';
import AdminTalentPoolPage from './pages/AdminTalentPoolPage';
import AdminPartnershipEnquiriesPage from './pages/AdminPartnershipEnquiriesPage';
import AdminRiskLeadsPage from './pages/AdminRiskLeadsPage';
import AdminRiskReportPage from './pages/AdminRiskReportPage';
import AdminContactEnquiriesPage from './pages/AdminContactEnquiriesPage';
import AdminSubmissionDetailPage from './pages/AdminSubmissionDetailPage';
import AdminFAQPage from './pages/AdminFAQPage';
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import AdminInsightsFeedbackPage from './pages/AdminInsightsFeedbackPage';
import AdminPostalTrackingPage from './pages/AdminPostalTrackingPage';
import AdminConsentDashboard from './pages/AdminConsentDashboard';
import AdminSiteBuilderPage from './pages/AdminSiteBuilderPage';
import AdminEnablementDashboard from './pages/AdminEnablementDashboard';
import AdminNotificationHealthPage from './pages/AdminNotificationHealthPage';
import AdminSystemHealthPage from './pages/AdminSystemHealthPage';
import AdminAutomationCentrePage from './pages/AdminAutomationCentrePage';
import AdminIncidentsPage from './pages/AdminIncidentsPage';
import AdminOperationalEvidenceTimelinePage from './pages/AdminOperationalEvidenceTimelinePage';
import AdminComplianceDecisionExplorerPage from './pages/AdminComplianceDecisionExplorerPage';
import AdminSecurityDashboardPage from './pages/AdminSecurityDashboardPage';
import AdminControlCentrePage from './pages/AdminControlCentrePage';
import AdminOpsOverviewPage from './pages/admin/AdminOpsOverviewPage';
import AdminOpsCompliancePage from './pages/admin/AdminOpsCompliancePage';
import AdminTenantDeliveryCompliancePage from './pages/admin/AdminTenantDeliveryCompliancePage';
import AdminComplianceRegistryListPage from './pages/admin/AdminComplianceRegistryListPage';
import AdminComplianceRegistryEditorPage from './pages/admin/AdminComplianceRegistryEditorPage';
import AdminComplianceRegistryPreviewPage from './pages/admin/AdminComplianceRegistryPreviewPage';
import AdminComplianceRegistryPublishQueuePage from './pages/admin/AdminComplianceRegistryPublishQueuePage';
import AdminOpsActionLinksPage from './pages/admin/AdminOpsActionLinksPage';
import AdminOpsAuditPage from './pages/admin/AdminOpsAuditPage';
import AdminOpsFeatureControlsPage from './pages/admin/AdminOpsFeatureControlsPage';
import AdminOpsRoiDiagnosticsPage from './pages/admin/AdminOpsRoiDiagnosticsPage';
import AdminCommunicationsPage from './pages/admin/AdminCommunicationsPage';
import AdminOpsContractorsPage from './pages/admin/AdminOpsContractorsPage';
import AdminIdentityLifecyclePage from './pages/admin/AdminIdentityLifecyclePage';
import AdminOpsMaintenancePage from './pages/admin/AdminOpsMaintenancePage';
import AdminRiskDashboardPage from './pages/admin/AdminRiskDashboardPage';
import AdminWorkOrderDetailPage from './pages/admin/AdminWorkOrderDetailPage';
import AdminReportingPage from './pages/AdminReportingPage';
import AdminClientControlPanelPage from './pages/AdminClientControlPanelPage';
import AdminUnresolvedEvidenceQueuePage from './pages/AdminUnresolvedEvidenceQueuePage';
import AdminEscalationReviewQueuePage from './pages/AdminEscalationReviewQueuePage';
import AdminExtractionQueuePage from './pages/AdminExtractionQueuePage';
import AdminTeamPage from './pages/AdminTeamPage';
import AdminPromptManagerPage from './pages/AdminPromptManagerPage';
import AdminClearFormUsersPage from './pages/AdminClearFormUsersPage';
import AdminClearFormDocumentsPage from './pages/AdminClearFormDocumentsPage';
import SharedReportPage from './pages/SharedReportPage';
import PublicKnowledgeBasePage from './pages/public/PublicKnowledgeBasePage';
import CookieBanner from './components/CookieBanner';
import ErrorBoundary from './components/ErrorBoundary';
import DebugPanel from './components/DebugPanel';
import ClientPortal from './components/ClientPortal';
import TenantsLayout from './components/TenantsLayout';
import ReportsLayout from './components/ReportsLayout';
import SettingsLayout from './components/SettingsLayout';
import HelpPage from './pages/HelpPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import { complianceTenantDeliveryRedirectTarget } from './utils/portalIaRedirects';

function RedirectToProperty() {
  const { propertyId } = useParams();
  return <Navigate to={propertyId ? `/properties/${propertyId}` : '/properties'} replace />;
}

/** Legacy IA: /compliance/tenant-delivery → /tenants/delivery (preserve query e.g. property_id). */
function ComplianceTenantDeliveryRedirect() {
  const { search } = useLocation();
  return <Navigate to={complianceTenantDeliveryRedirectTarget(search)} replace />;
}

/** Scroll window to top when the route (pathname) changes, so new pages start from the top. */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Records last client route for ErrorBoundary diagnostics (no PII beyond path). */
function ClientRouteTelemetry() {
  const location = useLocation();
  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        'cvp_nav_context',
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          ts: new Date().toISOString(),
        })
      );
    } catch {
      /* ignore */
    }
  }, [location.pathname, location.search, location.hash]);
  return null;
}

/** Legacy admin URL; canonical path is /admin/ops/maintenance/jobs/:workOrderId */
function AdminLegacyWorkOrderDetailRedirect() {
  const { workOrderId } = useParams();
  const id = workOrderId ? encodeURIComponent(workOrderId) : '';
  return <Navigate to={id ? `/admin/ops/maintenance/jobs/${id}` : '/admin/ops/maintenance'} replace />;
}

function App() {
  useEffect(() => {
    logBuildMetadata();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <LifecycleRuntimeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ClientRouteTelemetry />
          <ErrorBoundary>
          <div className="App">
            <Routes>
              {/* ========================================
                  CLEARFORM - SEPARATE PRODUCT
                  Completely isolated from Pleerity
                  ======================================== */}
              <Route path="/clearform/*" element={<Navigate to="/compliance-vault-pro" replace />} />

              {/* ========================================
                  PUBLIC WEBSITE ROUTES (NEW)
                  No auth required, SEO-optimized
                  ======================================== */}
              <Route path="/" element={<HomePage />} />
              <Route path="/demo" element={<Navigate to="/risk-check" replace />} />
              <Route path="/compliance-vault-pro" element={<CVPLandingPage />} />
              
              {/* CMS-Driven Marketing Pages */}
              <Route path="/services" element={<ServicesHubPageCMS />} />
              {/* Learn More: /services/detail/[slug] — must be before :categorySlug */}
              <Route path="/services/detail/:serviceSlug" element={<ServiceDetailPage />} />
              {/* Legacy route must come BEFORE dynamic routes */}
              <Route path="/services/catalogue" element={<ServicesCataloguePage />} />
              <Route path="/services/:categorySlug" element={<CategoryPageCMS />} />
              <Route path="/services/:categorySlug/:serviceSlug" element={<ServicePageCMS />} />
              
              <Route path="/order/:serviceCode" element={<ServiceOrderPage />} />
              <Route path="/order-success" element={<OrderSuccessPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/insights" element={<InsightsHubPage />} />
              <Route path="/insights/:slug" element={<InsightsHubPage />} />
              <Route path="/insights/category/:category" element={<InsightsHubPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/talent-pool" element={<TalentPoolWizard />} />
              <Route path="/partnerships" element={<PartnershipsPage />} />
              <Route path="/partnerships/enquiry" element={<PartnershipEnquiryForm />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/cookies" element={<CookiePolicyPage />} />
              
              {/* New Marketing Pages */}
              <Route path="/products/assurestack" element={<AssureStackPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/newsletter" element={<NewsletterPage />} />
              <Route path="/checklist-thank-you" element={<ChecklistThankYouPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/track/lead-activity" element={<LeadActivityTrackPage />} />
              <Route path="/risk-check" element={<RiskCheckPage />} />
              
              {/* Knowledge Base (Public) */}
              <Route path="/support/knowledge-base" element={<PublicKnowledgeBasePage />} />
              <Route path="/support/knowledge-base/:slug" element={<PublicKnowledgeBasePage />} />
              
              {/* Shared Reports (Public) */}
              <Route path="/shared/report/:shareId" element={<SharedReportPage />} />

              {/* View Order (public, token from delivery email) */}
              <Route path="/view-order" element={<ViewOrderPage />} />

              {/* ========================================
                  AUTH & ONBOARDING ROUTES
                  ======================================== */}
              <Route path="/login" element={<PortalSelectorPage />} />
              <Route path="/login/client" element={<ClientLoginPage />} />
              <Route path="/login/admin" element={<AdminLoginPage />} />
              <Route path="/admin/signin" element={<AdminLoginPage />} /> {/* Legacy admin route */}
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/contractor-set-password" element={<ContractorSetPasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              {/* Secure job link: single work order via token (no login) */}
              <Route path="/job" element={<JobPage />} />
              {/* Contractor portal (contractor_token after invite / activation) */}
              <Route path="/contractor/login" element={<ContractorLoginPage />} />
              <Route path="/contractor-login" element={<Navigate to="/contractor/login" replace />} />
              <Route path="/contractors/register" element={<ContractorRegisterPage />} />
              <Route path="/contractor" element={<ContractorDashboardPage />} />
              <Route path="/intake/start" element={<IntakePage />} />
              <Route path="/onboarding-status" element={<OnboardingStatusPage />} />
              <Route path="/onboarding/continue" element={<OnboardingContinuePage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/checkout/cancel" element={<Navigate to="/intake/start" replace />} />

              {/* ========================================
                  CLIENT PORTAL ROUTES (Enterprise UI)
                  /dashboard, /properties, /requirements, etc. + /app/* redirects
                  ======================================== */}
            <Route path="/dashboard" element={<ClientPortal><ClientDashboard /></ClientPortal>} />
            <Route path="/today" element={<ClientPortal><ClientTasksPage /></ClientPortal>} />
            <Route path="/work-queue" element={<ClientPortal><ClientWorkQueuePage /></ClientPortal>} />
            <Route path="/command-center" element={<ClientPortal><ClientCommandCenterPage /></ClientPortal>} />
            <Route path="/tasks" element={<Navigate to="/today" replace />} />
            <Route path="/properties" element={<ClientPortal><PropertiesPage /></ClientPortal>} />
            <Route path="/properties/:propertyId" element={<ClientPortal><PropertyDetailPage /></ClientPortal>} />
            <Route path="/properties/create" element={<ClientPortal><PropertyCreatePage /></ClientPortal>} />
            <Route path="/properties/import" element={<ClientPortal><BulkPropertyImportPage /></ClientPortal>} />
            <Route path="/requirements" element={<ClientPortal><RequirementsPage /></ClientPortal>} />
            <Route path="/documents" element={<ClientPortal><DocumentsPage /></ClientPortal>} />
            <Route path="/audit-log" element={<ClientPortal><ClientAuditLogPage /></ClientPortal>} />
            <Route path="/documents/bulk-upload" element={<ClientPortal><BulkUploadPage /></ClientPortal>} />
            <Route path="/calendar" element={<ClientPortal><CalendarPage /></ClientPortal>} />
            <Route path="/reports" element={<ClientPortal><ReportsLayout /></ClientPortal>}>
              <Route index element={<ReportsPage />} />
              <Route
                path="audit-pack"
                element={
                  <AccountCapabilityProtectedRoute requiredFeature="reports_pdf">
                    <ReportsAuditPackPage />
                  </AccountCapabilityProtectedRoute>
                }
              />
              <Route path="property-evidence" element={<ReportsPropertyEvidencePage />} />
            </Route>
            <Route path="/compliance-score" element={<ClientPortal><ComplianceScorePage /></ClientPortal>} />
            <Route path="/assistant" element={<ClientPortal><AssistantPage /></ClientPortal>} />
            <Route path="/help" element={<ClientPortal><HelpPage /></ClientPortal>} />
            <Route path="/settings" element={<ClientPortal><SettingsLayout /></ClientPortal>}>
              <Route index element={<Navigate to="/settings/profile" replace />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="jurisdiction" element={<JurisdictionSettingsPage />} />
              <Route path="notifications" element={<NotificationPreferencesPage />} />
              <Route path="inbox" element={<ClientNotificationInboxPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="billing/receipts" element={<Navigate to="/settings/billing?tab=account" replace />} />
              <Route path="branding" element={<AccountCapabilityProtectedRoute requiredFeature="white_label_reports"><BrandingSettingsPage /></AccountCapabilityProtectedRoute>} />
            </Route>
            <Route path="/tenant" element={<ClientPortal><Outlet /></ClientPortal>}>
              <Route index element={<TenantDashboard />} />
              <Route path="properties" element={<TenantPropertiesPage />} />
              <Route path="properties/:propertyId" element={<TenantPropertyDetailPage />} />
              <Route path="settings" element={<TenantSettingsPage />} />
            </Route>
            <Route
              path="/tenants"
              element={
                <ClientPortal>
                  <AccountCapabilityProtectedRoute requiredFeature="tenant_portal">
                    <TenantsLayout />
                  </AccountCapabilityProtectedRoute>
                </ClientPortal>
              }
            >
              <Route index element={<TenantManagementPage section="list" />} />
              <Route path="messages" element={<TenantManagementPage section="messages" />} />
              <Route path="certificate-requests" element={<TenantManagementPage section="certificate-requests" />} />
              <Route path="delivery" element={<ClientTenantComplianceDeliveryPage />} />
            </Route>
            <Route path="/compliance/tenant-delivery" element={<ClientPortal><ComplianceTenantDeliveryRedirect /></ClientPortal>} />
            <Route path="/integrations" element={<ClientPortal><AccountCapabilityProtectedRoute requiredFeature="webhooks"><IntegrationsPage /></AccountCapabilityProtectedRoute></ClientPortal>} />
            <Route path="/orders/:orderId/provide-info" element={<ClientPortal><ClientProvideInfoPage /></ClientPortal>} />
            <Route path="/orders" element={<ClientPortal><ClientOrdersPage /></ClientPortal>} />
            {/* Operations (unified) */}
            <Route path="/operations/issues" element={<ClientPortal><ClientIssuesPage /></ClientPortal>} />
            <Route path="/operations/issues/:issueId" element={<ClientPortal><ClientIssueDetailPage /></ClientPortal>} />
            <Route path="/operations/work-orders" element={<ClientPortal><ClientMaintenancePage /></ClientPortal>} />
            <Route path="/operations/jobs/:jobId" element={<ClientPortal><ClientJobDetailPage /></ClientPortal>} />
            <Route path="/operations/contractors" element={<ClientPortal><ClientContractorsPage /></ClientPortal>} />
            <Route path="/operations/risk-signals" element={<ClientPortal><ClientRiskSignalsPage /></ClientPortal>} />
            <Route path="/operations/rent" element={<ClientPortal><ClientRentOperationsPage /></ClientPortal>} />
            <Route path="/operations/expenses" element={<Navigate to="/operations/rent?tab=expenses" replace />} />
            <Route path="/operations/approvals" element={<ClientPortal><ClientApprovalsPage /></ClientPortal>} />
            <Route path="/operations/compliance-review" element={<ClientPortal><ClientOrgComplianceReviewPage /></ClientPortal>} />
            {/* Legacy maintenance/contractors → redirect to operations */}
            <Route path="/maintenance" element={<Navigate to="/operations/work-orders" replace />} />
            <Route path="/contractors" element={<Navigate to="/operations/contractors" replace />} />

            {/* Redirect legacy /app/* to new paths */}
            <Route path="/app/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/app/today" element={<Navigate to="/today" replace />} />
            <Route path="/app/command-center" element={<Navigate to="/command-center" replace />} />
            <Route path="/app/tasks" element={<Navigate to="/today" replace />} />
            <Route path="/app/assistant" element={<Navigate to="/assistant" replace />} />
            <Route path="/app/settings" element={<Navigate to="/settings" replace />} />
            <Route path="/app/profile" element={<Navigate to="/settings/profile" replace />} />
            <Route path="/app/notifications" element={<Navigate to="/settings/notifications" replace />} />
            <Route path="/app/inbox" element={<Navigate to="/settings/inbox" replace />} />
            <Route path="/app/calendar" element={<Navigate to="/calendar" replace />} />
            <Route path="/app/properties/create" element={<Navigate to="/properties/create" replace />} />
            <Route path="/app/properties" element={<Navigate to="/properties" replace />} />
            <Route path="/app/property/:propertyId" element={<RedirectToProperty />} />
            <Route path="/app/requirements" element={<Navigate to="/requirements" replace />} />
            <Route path="/app/compliance-score" element={<Navigate to="/compliance-score" replace />} />
            <Route path="/app/documents" element={<Navigate to="/documents" replace />} />
            <Route path="/app/documents/bulk-upload" element={<Navigate to="/documents/bulk-upload" replace />} />
            <Route path="/app/audit-log" element={<Navigate to="/audit-log" replace />} />
            <Route path="/app/reports" element={<Navigate to="/reports" replace />} />
            <Route path="/app/tenant" element={<Navigate to="/tenant" replace />} />
            <Route path="/app/tenants" element={<Navigate to="/tenants" replace />} />
            <Route path="/app/compliance/tenant-delivery" element={<ClientPortal><ComplianceTenantDeliveryRedirect /></ClientPortal>} />
            <Route path="/app/properties/import" element={<Navigate to="/properties/import" replace />} />
            <Route path="/app/integrations" element={<Navigate to="/integrations" replace />} />
            <Route path="/app/billing" element={<Navigate to="/settings/billing" replace />} />
            <Route path="/app/billing/receipts" element={<Navigate to="/settings/billing?tab=account" replace />} />
            <Route path="/app/settings/jurisdiction" element={<Navigate to="/settings/jurisdiction" replace />} />
            <Route path="/app/settings/branding" element={<Navigate to="/settings/branding" replace />} />
            <Route path="/app/branding" element={<Navigate to="/settings/branding" replace />} />
            <Route path="/app/orders/:orderId/provide-info" element={<Navigate to="/orders/:orderId/provide-info" replace />} />
            <Route path="/app/orders" element={<Navigate to="/orders" replace />} />
            <Route path="/app/maintenance" element={<Navigate to="/operations/work-orders" replace />} />
            <Route path="/app/contractors" element={<Navigate to="/operations/contractors" replace />} />

            {/* ========================================
                  ORDER INTAKE WIZARD (Public)
                  ======================================== */}
            <Route 
              path="/order/intake" 
              element={<UnifiedIntakeWizard />} 
            />
            <Route 
              path="/order/intake/:draftId" 
              element={<UnifiedIntakeWizard />} 
            />
            <Route 
              path="/order/checkout" 
              element={<OrderCheckoutPage />} 
            />
            <Route 
              path="/order/confirmation" 
              element={<OrderConfirmationPage />} 
            />
            <Route path="/order/provide-info" element={<OrderProvideInfoPublicPage />} />

            {/* ========================================
                  ADMIN PORTAL ROUTES (Protected)
                  Existing CVP functionality - DO NOT MODIFY
                  ======================================== */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/assistant" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminAssistantPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/billing" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBillingPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/discovery/review"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDiscoveryReviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pilot-invites"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPilotInvitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pilot-invites/:code"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPilotInviteDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pilot-operations"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPilotOperationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pilot-operations/anomalies"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPilotAnomaliesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pilot-operations/accounts/:clientId"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPilotAccountDetailPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/orders" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminOrdersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/services" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminServiceCataloguePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/blog" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBlogPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/notifications/preferences" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminNotificationPreferencesPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/notifications/inbox"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminNotificationInboxPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/intake-schema" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminIntakeSchemaPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/intake-schema/:serviceCode" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminIntakeSchemaPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/analytics" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminAnalyticsDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/analytics/executive" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminExecutiveOverviewPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/analytics/generation-health"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminGenerationHealthPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/reporting" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminReportingPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/team" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminTeamPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/prompts" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPromptManagerPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/support" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSupportPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/extraction-queue" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminExtractionQueuePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/knowledge-base" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminKnowledgeBasePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/support/responses" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCannedResponsesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings/legal" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLegalContentPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/admin/leads" element={<ProtectedRoute requireAdmin><AdminLeadsPage /></ProtectedRoute>} />
            <Route path="/admin/pending-payments" element={<ProtectedRoute requireAdmin><Navigate to="/admin/billing?tab=pending-payments" replace /></ProtectedRoute>} />
            <Route path="/admin/talent-pool" element={<ProtectedRoute requireAdmin><AdminTalentPoolPage /></ProtectedRoute>} />
            <Route path="/admin/partnership-enquiries" element={<ProtectedRoute requireAdmin><AdminPartnershipEnquiriesPage /></ProtectedRoute>} />
            <Route path="/admin/risk-leads/report/:leadId" element={<ProtectedRoute requireAdmin><AdminRiskReportPage /></ProtectedRoute>} />
            <Route path="/admin/risk-leads" element={<ProtectedRoute requireAdmin><AdminRiskLeadsPage /></ProtectedRoute>} />
            <Route path="/admin/inbox/enquiries" element={<ProtectedRoute requireAdmin><AdminContactEnquiriesPage /></ProtectedRoute>} />
            <Route path="/admin/submissions/:type/:id" element={<ProtectedRoute requireAdmin><AdminSubmissionDetailPage /></ProtectedRoute>} />
            <Route path="/admin/content/faqs" element={<ProtectedRoute requireAdmin><AdminFAQPage /></ProtectedRoute>} />
            <Route path="/admin/marketing/newsletter" element={<ProtectedRoute requireAdmin><AdminNewsletterPage /></ProtectedRoute>} />
            <Route path="/admin/content/feedback" element={<ProtectedRoute requireAdmin><AdminInsightsFeedbackPage /></ProtectedRoute>} />
            <Route path="/admin/postal-tracking" element={<ProtectedRoute requireAdmin><AdminPostalTrackingPage /></ProtectedRoute>} />
            <Route path="/admin/clients/:clientId" element={<ProtectedRoute requireAdmin><AdminClientControlPanelPage /></ProtectedRoute>} />
            <Route
              path="/admin/compliance-evidence/escalation-queue"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEscalationReviewQueuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/documents/unresolved-queue"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminUnresolvedEvidenceQueuePage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/privacy/consent" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminConsentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/site-builder" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSiteBuilderPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/enablement" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEnablementDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/notification-health" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminNotificationHealthPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/admin/system-health" element={<ProtectedRoute requireAdmin><AdminSystemHealthPage /></ProtectedRoute>} />
            <Route path="/admin/control-centre" element={<ProtectedRoute requireAdmin><AdminControlCentrePage /></ProtectedRoute>} />
            <Route path="/admin/security" element={<ProtectedRoute requireAdmin><AdminSecurityDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/automation" element={<ProtectedRoute requireAdmin><AdminAutomationCentrePage /></ProtectedRoute>} />
            <Route path="/admin/incidents" element={<ProtectedRoute requireAdmin><AdminIncidentsPage /></ProtectedRoute>} />
            <Route path="/admin/ops/evidence-timeline" element={<ProtectedRoute requireAdmin><AdminOperationalEvidenceTimelinePage /></ProtectedRoute>} />
            <Route path="/admin/compliance/decisions" element={<ProtectedRoute requireAdmin><AdminComplianceDecisionExplorerPage /></ProtectedRoute>} />
            {/* Operations & Compliance */}
            <Route path="/admin/ops" element={<ProtectedRoute requireAdmin><AdminOpsOverviewPage /></ProtectedRoute>} />
            <Route path="/admin/ops/compliance" element={<ProtectedRoute requireAdmin><AdminOpsCompliancePage /></ProtectedRoute>} />
            <Route
              path="/admin/compliance/tenant-delivery"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminTenantDeliveryCompliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/compliance/registry/preview"
              element={<ProtectedRoute requireAdmin><AdminComplianceRegistryPreviewPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/compliance/registry/publish-queue"
              element={<ProtectedRoute requireAdmin><AdminComplianceRegistryPublishQueuePage /></ProtectedRoute>}
            />
            <Route
              path="/admin/compliance/registry/:entryId"
              element={<ProtectedRoute requireAdmin><AdminComplianceRegistryEditorPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/compliance/registry"
              element={<ProtectedRoute requireAdmin><AdminComplianceRegistryListPage /></ProtectedRoute>}
            />
            <Route path="/admin/ops/action-links" element={<ProtectedRoute requireAdmin><AdminOpsActionLinksPage /></ProtectedRoute>} />
            <Route path="/admin/ops/maintenance" element={<ProtectedRoute requireAdmin><AdminOpsMaintenancePage /></ProtectedRoute>} />
            <Route path="/admin/ops/maintenance/jobs/:workOrderId" element={<ProtectedRoute requireAdmin><AdminWorkOrderDetailPage /></ProtectedRoute>} />
            <Route path="/admin/ops/maintenance/work-orders/:workOrderId" element={<ProtectedRoute requireAdmin><AdminLegacyWorkOrderDetailRedirect /></ProtectedRoute>} />
            <Route path="/admin/ops/contractors" element={<ProtectedRoute requireAdmin><AdminOpsContractorsPage /></ProtectedRoute>} />
            <Route path="/admin/ops/identities" element={<ProtectedRoute requireAdmin><AdminIdentityLifecyclePage /></ProtectedRoute>} />
            <Route path="/admin/ops/risk" element={<ProtectedRoute requireAdmin><AdminRiskDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/ops/audit" element={<ProtectedRoute requireAdmin><AdminOpsAuditPage /></ProtectedRoute>} />
            <Route path="/admin/ops/feature-controls" element={<ProtectedRoute requireAdmin><AdminOpsFeatureControlsPage /></ProtectedRoute>} />
            <Route path="/admin/ops/roi-diagnostics" element={<ProtectedRoute requireAdmin><AdminOpsRoiDiagnosticsPage /></ProtectedRoute>} />
            <Route path="/admin/communications" element={<ProtectedRoute requireAdmin><AdminCommunicationsPage /></ProtectedRoute>} />

            {/* ClearForm Admin Routes */}
            <Route 
              path="/admin/clearform/users" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminClearFormUsersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/clearform/documents" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminClearFormDocumentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/clearform/organizations" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminClearFormUsersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/clearform/document-types" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminClearFormDocumentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/clearform/audit" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminClearFormUsersPage />
                </ProtectedRoute>
              } 
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
          <TawkToWidget />
          <CookieBanner />
          <DebugPanel />
        </div>
          </ErrorBoundary>
        </BrowserRouter>
        </LifecycleRuntimeProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
